import { useState, useEffect } from 'react';
import { Button } from './ui/button';
import digitechLogo from '/logo.png';

// Tambahan untuk ikon hamburger
import { Menu as MenuIcon, X as CloseIcon } from 'lucide-react';

const navItems = [
  { id: 'home', label: 'Home' },
  { id: 'about', label: 'About' },
  { id: 'culture', label: 'Culture' },
  { id: 'services', label: 'Services' },
  { id: 'products', label: 'Products' },
  { id: 'clients', label: 'Clients' },
  { id: 'team', label: 'Team' },
  { id: 'contact', label: 'Contact' }
];

export function Navigation() {
  const [activeSection, setActiveSection] = useState('home');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [isClosing, setIsClosing] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id);
          }
        });
      },
      { 
        threshold: [0.1, 0.3, 0.5, 0.7, 0.9],
        rootMargin: '-10% 0px -10% 0px'
      }
    );

    // Pastikan semua section diamati
    navItems.forEach((item) => {
      const element = document.getElementById(item.id);
      if (element) {
        observer.observe(element);
      }
    });

    // Fallback scroll event listener untuk memastikan navigation tetap berfungsi
    const handleScroll = () => {
      const scrollPosition = window.scrollY + window.innerHeight / 2;
      
      // Cari section yang sedang aktif berdasarkan scroll position
      for (let i = navItems.length - 1; i >= 0; i--) {
        const element = document.getElementById(navItems[i].id);
        if (element) {
          const elementTop = element.offsetTop;
          const elementBottom = elementTop + element.offsetHeight;
          
          if (scrollPosition >= elementTop && scrollPosition <= elementBottom) {
            setActiveSection(navItems[i].id);
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      observer.disconnect();
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    element?.scrollIntoView({ behavior: 'smooth' });
    setDrawerOpen(false); // Tutup drawer jika di mobile
  };

  const closeDrawer = () => {
    setIsClosing(true);
    setTimeout(() => {
      setDrawerOpen(false);
      setIsClosing(false);
    }, 300);
  };

  return (
    <nav className="fixed top-0 left-0 w-full z-50 bg-black/80 backdrop-blur-md ">
      <div className="container mx-auto px-4 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 rounded-lg overflow-hidden bg-gradient-to-br from-primary/20 to-accent/10 p-2 tech-glow flex items-center justify-center">
              <img 
                src={digitechLogo} 
                alt="DIGITECH Logo" 
                className="w-full h-full object-contain object-center drop-shadow-md"
              />
            </div>
            <div className="flex flex-col">
              <div className="font-orbitron text-xl font-bold text-primary flex items-center">
                DI<span className="text-accent-2">ROI</span>
              </div>
              <div className="text-xs text-muted-foreground font-light">
                Part of Perahu Group
              </div>
            </div>
          </div>
          {/* Tombol hamburger hanya di mobile */}
          <button
            className="md:hidden p-2 rounded focus:outline-none focus:ring-2 focus:ring-primary"
            onClick={() => setDrawerOpen(true)}
            aria-label="Open menu"
          >
            <MenuIcon className="w-7 h-7 text-primary" />
          </button>
          {/* Menu desktop */}
          <div className="hidden md:flex space-x-1">
            {navItems.map((item) => (
              <Button
                key={item.id}
                variant={activeSection === item.id ? 'default' : 'ghost'}
                size="sm"
                onClick={() => scrollToSection(item.id)}
                className={`
                  transition-all duration-300 font-orbitron
                  ${activeSection === item.id 
                    ? 'bg-primary text-primary-foreground tech-glow' 
                    : 'text-muted-foreground hover:text-primary hover:bg-primary/10'
                  }
                `}
              >
                {item.label}
              </Button>
            ))}
          </div>
        </div>
      </div>
      {/* Drawer menu mobile */}
      {drawerOpen && (
        <>
          {/* Overlay */}
          <div
            className="fixed inset-0 z-[99] bg-black/60 backdrop-blur-sm transition-opacity duration-300"
            onClick={closeDrawer}
            aria-label="Close menu overlay"
          />
          {/* Drawer */}
          <div
            className={`fixed top-0 right-0 h-full w-3/4 max-w-xs z-[100] bg-black/95 shadow-lg border-l border-primary flex flex-col transition-transform duration-300 transform ${
              isClosing ? 'translate-x-full' : 'translate-x-0'
            }`}
            style={{ minWidth: '220px' }}
          >
            <div className="flex items-center justify-between p-4 border-b border-primary">
              <div className="flex flex-col">
                <div className="font-orbitron text-lg font-bold text-primary flex items-center">
                  DI<span className="text-accent-2">ROI</span>
                </div>
                <div className="text-xs text-muted-foreground font-light">
                  Part of Perahu Group
                </div>
              </div>
              <button
                className="p-2 rounded focus:outline-none focus:ring-2 focus:ring-primary"
                onClick={closeDrawer}
                aria-label="Close menu"
              >
                <CloseIcon className="w-6 h-6 text-primary" />
              </button>
            </div>
            <nav className="flex-1 flex flex-col items-start justify-start space-y-2 px-4 py-6">
              {navItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => scrollToSection(item.id)}
                  className={`w-full text-left py-2 px-2 rounded font-orbitron text-base transition-all duration-200
                    ${activeSection === item.id 
                      ? 'bg-primary text-primary-foreground tech-glow' 
                      : 'bg-black/80 text-muted-foreground hover:text-primary hover:bg-primary/10'
                    }
                  `}
                >
                  {item.label}
                </button>
              ))}
            </nav>
          </div>
        </>
      )}
    </nav>
  );
}